import { useNavigation } from "@react-navigation/native";
import React, { memo, useEffect, useMemo, useState } from "react";
import {
  StyleSheet,
  TouchableOpacity,
  View,
  Dimensions,
  PixelRatio,
  SectionList,
  Image,
  ActivityIndicator,
  TouchableWithoutFeedback,
} from "react-native";

import t from "@constants/localization";
import Text from "@components/AppText";
import colors from "@constants/color";
import analytics from "@services/analytics";
import { MY_DELIVERY_EVENTS } from "@constants/analyticsEventsCT";
import CallIcon from "@assets/svg/newCall";
import Tick from "@assets/svg/MyDeliveries/Tick";
import { getReturnDetailsV3 } from "@services/api";
import { Gullak, OrderReturnStatus } from "@assets/svg/MyDeliveries";
import { getDayMonth } from "@utils/getFormattedDate";
import HeaderWithBackAndTitle from "@components/HeaderWithBackAndTitle";
import handleCallButtonPress from "@utils/handleCallButtonPress";
import { getReturnAmount, getItemsNotReturnedText } from "./utils";

const PATH = "screens/MyDeliveries/DeliveryReturnsList";
const { width } = Dimensions.get("window");
const x = width / 360;

const NewTag = () => {
  return (
    <View style={styles.newTag}>
      <Text style={styles.newTagText}>नया</Text>
    </View>
  );
};

const onPressCall = (item) => {
  handleCallButtonPress(item?.user_phone, MY_DELIVERY_EVENTS.DB_CALLED, {
    user_id: item?.user_id,
  });
};

function normalize(size) {
  const newSize = size * x;
  return Math.round(PixelRatio.roundToNearestPixel(newSize));
}

const OrdersList = ({ route }) => {
  const navigation = useNavigation();
  const [ordersData, setOrdersData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const transferTypes = [
    "cashToCustomer",
    "cashToClGullak",
    "cashRefundedToCustomer",
    "cashRefundedToClGullak",
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await getReturnDetailsV3({
          date: route?.params?.date,
          spokeName: route?.params?.spokeName,
        });
        setOrdersData(data);
      } catch (error) {
        console.log(error);
      }
      setIsLoading(false);
    };
    fetchData();
  }, [route?.params?.date, route?.params?.spokeName]);

  const sortUpdates = useMemo(
    () =>
      function () {
        const sortedUpdateObjects = {};
        const ordersDataArray = Object.keys(ordersData);
        ordersDataArray.forEach((key) => {
          if (
            Array.isArray(ordersData[key]) // && ordersData[key].length !== 0
          ) {
            sortedUpdateObjects[key] = ordersData[key];
          }
        });
        return Object.keys(sortedUpdateObjects).map((key) => {
          return {
            key,
            data: sortedUpdateObjects[key],
            crates: ordersData?.returnCratesList[key],
          };
        });
      },
    [ordersData],
  );

  const renderItem = ({ item, section }) => (
    <Card item={item} section={section} />
  );

  const headingText = ({ key, crates, items }) => {
    switch (key) {
      case "itemsNotReturned":
        return getItemsNotReturnedText({
          items,
          crates: crates?.length,
        });

      case "itemsToBeReturned":
        return t(
          `${PATH}/itemsToBeReturned/${items === 1 ? "singular" : "plural"}`,
        );

      case "itemsReturned":
        return t(
          `${PATH}/itemsReturned/${items === 1 ? "singular" : "plural"}`,
          {
            count: items,
          },
        );

      default:
        return "";
    }
  };

  const renderSection = ({ section: { key, data = [], crates = [] } }) => {
    const crateText = crates.length
      ? `${t(`${PATH}/crate`)} :${crates.map((v) => ` ${v}`)}`
      : "";
    return data.length || crates?.length ? (
      <>
        <View
          style={[
            styles.sectionHeaderContainer,
            {
              backgroundColor:
                key === "itemsNotReturned" ? "#FFEAEA" : "transparent",
            },
          ]}
        >
          <View style={styles.sectionHeader}>
            <OrderReturnStatus orderStatus={key} />
            <Text
              style={[
                styles.sectionHeaderText,
                {
                  color:
                    key === "itemsNotReturned"
                      ? colors.plain_red
                      : key === "itemsToBeReturned"
                        ? colors.black
                        : colors.plain_green,
                },
              ]}
            >
              {headingText({
                key,
                crates,
                items: ordersData?.returnsCount[key],
              })}
            </Text>
          </View>
          <View style={{ flexDirection: "row" }}>
            {crateText ? (
              <View style={styles.crateContainer}>
                <Text style={styles.crateText}> {crateText} </Text>
              </View>
            ) : null}
          </View>
        </View>
      </>
    ) : null;
  };

  const keyExtractor = (item, index) => `${item.user_id}-${index}`;

  const onCardPress = (item) => {
    analytics.trackEvent(MY_DELIVERY_EVENTS.CUSTOMER_TILE_CLICKED, {
      user_id: item.user_id,
      screen: "Delivery Details",
      tab: route?.params?.tab,
      section: "Return",
    });
  };

  const Card = memo(({ item, section }) => {
    const renderCardData = (key) => {
      if (item[key]) {
        return (
          <View key={key} style={styles.cardFooterContainer}>
            <View style={styles.view2}>
              <View>
                <Text style={styles.text2}>{t(`${PATH}/return`)}</Text>
                <Text style={styles.text3}>{item[key]?.returnItemsCount}</Text>
              </View>
              <View style={styles.separator} />
              <View style={styles.cardItemSection}>
                <View>
                  <Text style={styles.text2}>{t(`${PATH}/refund`)}</Text>
                  <Text style={styles.text3}>
                    {t(`${PATH}/rs`)}
                    {item[key]?.refundAmount}
                  </Text>
                </View>
                {section.key !== "itemsNotReturned" && (
                  <PaymentMode type={key} />
                )}
              </View>
            </View>
            <View style={styles.horizontalSeparator} />
            {item[key]?.returnItemsList?.map((ITEM, idx) => {
              return <ItemCard key={ITEM.short_name ?? idx} ITEM={ITEM} />;
            })}
          </View>
        );
      }
    };
    return (
      <TouchableWithoutFeedback onPress={() => onCardPress(item)}>
        <View
          style={[
            {
              backgroundColor:
                section.key === "itemsNotReturned" ? "#FFEAEA" : "transparent",
            },
            styles.cardContainer,
          ]}
        >
          <View style={styles.listView}>
            {item?.new_user && <NewTag />}
            <View style={styles.cardHeaderContainer}>
              <View style={styles.view1}>
                <Text style={styles.name}>{item?.user_name}</Text>
                <TouchableOpacity
                  testID="call"
                  onPress={() => onPressCall(item)}
                >
                  <CallIcon
                    strokeColor="#2195F2"
                    width={normalize(17)}
                    height={normalize(17)}
                  />
                </TouchableOpacity>
              </View>
              <Text style={styles.address}>{item?.cx_formatted_address}</Text>
            </View>
            {transferTypes.map(renderCardData)}
          </View>
        </View>
      </TouchableWithoutFeedback>
    );
  });

  return (
    <View style={styles.container}>
      <HeaderWithBackAndTitle
        headertitle={t(`${PATH}/returnDetails`, {
          date: getDayMonth(route?.params?.date),
        })}
        headerColor="#fff"
        hideShadow={false}
        navigation={navigation}
        route={route}
      />
      {isLoading ? (
        <View style={styles.loadingView}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      ) : (
        <SectionList
          renderSectionHeader={renderSection}
          keyExtractor={keyExtractor}
          sections={sortUpdates()}
          renderItem={renderItem}
          contentContainerStyle={{ paddingBottom: 16 }}
        />
      )}
      <View style={styles.bottomStrip}>
        <Gullak
          status={
            ordersData?.amount?.cashToClGullak === 0 ? "Returned" : "Return"
          }
        />
        <Text style={styles.bottomStripText}>
          {getReturnAmount(ordersData?.amount).key === "cashRefundedToClGullak"
            ? t(`${PATH}/cashRefundedToClGullak2`)
            : t(`${PATH}/cashToClGullak2`)}
          <Text style={styles.amount}>
            {t(`${PATH}/rs`)}
            {getReturnAmount(ordersData?.amount).amount ?? 0}
          </Text>
        </Text>
      </View>
    </View>
  );
};

const ItemCard = ({ ITEM }) => {
  return (
    <View key={ITEM.short_name} style={styles.itemContainer}>
      <Image
        source={{ uri: ITEM.product_image }}
        style={styles.itemImage}
        resizeMode="contain"
        alt="Product Image"
      />
      <View style={styles.itemDescContainer}>
        <Text style={styles.itemName}>{ITEM.short_name}</Text>
        <View style={styles.descContainer}>
          <Text style={styles.itemDescText}>
            {t(`${PATH}/rs`)}
            <Text
              style={[
                styles.itemDescText,
                { fontFamily: "NotoSansDevanagari-Bold" },
              ]}
            >
              {ITEM.price_after_discount}
            </Text>
          </Text>
          <View style={styles.itemDescSeparator} />
          <Text style={styles.itemDescText}>
            Qty •{" "}
            <Text
              style={[
                styles.itemDescText,
                { fontFamily: "NotoSansDevanagari-Bold" },
              ]}
            >
              {ITEM.quantity}
            </Text>
          </Text>
        </View>
      </View>
    </View>
  );
};

const PaymentMode = ({ type }) => {
  if (type === "cashRefundedToCustomer" || type === "cashToCustomer") {
    return (
      <View style={styles.onlineTag}>
        <Tick visible={type === "cashRefundedToCustomer"} />
        <Text style={styles.online}>{t(`${PATH}/${type}`)}</Text>
      </View>
    );
  }
  return (
    <View style={styles.cashTag}>
      <Tick fill={colors.white} visible={type === "cashRefundedToClGullak"} />
      <Text style={styles.cash}>{t(`${PATH}/${type}`)}</Text>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  listView: {
    backgroundColor: colors.white,
    borderRadius: 10,
    elevation: 4,
    overflow: "hidden",
  },
  view1: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
  },
  view2: {
    flexDirection: "row",
    marginTop: 12,
    alignItems: "center",
  },
  text2: {
    fontSize: 14,
    lineHeight: 18,
    color: "rgba(0, 0, 0, 0.56)",
  },
  text3: {
    marginTop: 2,
    fontSize: 16,
    lineHeight: 20,
    fontFamily: "NotoSansDevanagari-Bold",
  },
  name: {
    fontSize: 14,
    lineHeight: 24,
    color: "black",
    fontWeight: "bold",
  },
  sectionHeaderContainer: {
    paddingHorizontal: 16,
    paddingVertical: 16,
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
  },
  newTagText: {
    fontFamily: "NotoSansDevanagari-Bold",
    fontWeight: "700",
    fontSize: 11,
    color: "#FFFFFF",
    textAlign: "center",
  },
  newTag: {
    position: "absolute",
    left: -20,
    top: 3,
    height: 15,
    width: 60,
    alignItems: "center",
    justifyContent: "center",
    transform: [{ rotate: "-45deg" }],
    backgroundColor: "#00BA6E",
  },
  loadingView: {
    justifyContent: "center",
    alignItems: "center",
    flex: 1,
    backgroundColor: colors.white,
  },
  sectionHeaderText: {
    fontSize: 16,
    lineHeight: 24,
    fontFamily: "NotoSansDevanagari-Bold",
    marginHorizontal: 6,
  },
  onlineTag: {
    borderRadius: 20,
    borderWidth: 1,
    paddingHorizontal: 8,
    paddingVertical: 2,
    alignItems: "center",
    marginHorizontal: 8,
    justifyContent: "center",
    borderColor: colors.lighterBlack,
    flexDirection: "row",
  },
  cashTag: {
    backgroundColor: colors.plain_green,
    borderRadius: 40,
    paddingHorizontal: 8,
    paddingVertical: 2,
    alignItems: "center",
    marginHorizontal: 8,
    justifyContent: "center",
    flexDirection: "row",
  },
  online: {
    color: colors.lightBlack,
    fontSize: 10,
    lineHeight: 16,
    fontFamily: "NotoSansDevanagari-Bold",
    marginLeft: 4,
  },
  cash: {
    color: "white",
    fontSize: 10,
    lineHeight: 16,
    fontFamily: "NotoSansDevanagari-Bold",
  },
  address: {
    maxWidth: "80%",
    color: colors.lightBlack,
    fontSize: 14,
    fontFamily: "NotoSansDevanagari-Regular",
  },
  separator: {
    backgroundColor: colors.lighterBlack,
    height: 24,
    width: 1,
    marginHorizontal: 24,
  },
  horizontalSeparator: {
    backgroundColor: colors.lighterBlack,
    height: 1,
    width: "100%",
    marginTop: 16,
  },
  itemContainer: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 16,
  },
  itemImage: {
    height: 60,
    width: 60,
  },
  itemName: {
    fontSize: 14,
    fontFamily: "NotoSansDevanagari-Regular",
    color: colors.mediumBlack,
    lineHeight: 24,
  },
  itemDescContainer: {
    paddingHorizontal: 16,
    width: "80%",
  },
  itemDescText: {
    fontSize: 16,
    fontFamily: "NotoSansDevanagari-Regular",
    lineHeight: 24,
  },
  itemDescSeparator: {
    backgroundColor: colors.lightMediumBlack,
    height: 22,
    width: 1,
    marginHorizontal: 16,
  },
  descContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  bottomStrip: {
    padding: 16,
    backgroundColor: colors.white,
    flexDirection: "row",
    alignItems: "center",
    elevation: 20,
  },
  bottomStripText: {
    fontSize: 14,
    lineHeight: 18,
    marginHorizontal: 8,
    color: colors.lightBlack,
  },
  amount: {
    fontFamily: "NotoSansDevanagari-Bold",
    color: colors.black,
    fontSize: 16,
    lineHeight: 24,
  },
  cardContainer: {
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
  cardItemSection: {
    flexDirection: "row",
    alignItems: "flex-start",
  },
  cardHeaderContainer: {
    padding: 16,
  },
  cardFooterContainer: {
    paddingHorizontal: 16,
  },
  crateText: {
    fontSize: 14,
    fontFamily: "NotoSansDevanagari-SemiBold",
    lineHeight: 20,
    color: "white",
  },
  crateContainer: {
    backgroundColor: "rgba(0, 0, 0, 0.70)",
    marginHorizontal: 24,
    paddingHorizontal: 12,
    paddingTop: 4,
    paddingBottom: 2,
    borderRadius: 18,
    marginTop: 4,
    flexDirection: "row",
  },
});

export default OrdersList;
